import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import ContentCard from "../../../components/misc/ContentCard";
import TransferList from "../../../components/misc/TransferableLists";
import Text from "../../../styleguide/Text";
import CustomButton from "../../../components/CustomButton";
import Dropdown, { DropdownValue } from "../../../components/misc/Dropdown";

import useRoutesHelper from "../../../hooks/useRoutesHelper";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  getDeliveryInvoices,
  postSplitDeliveryArrangements,
} from "../../../redux/api/picking.api";
import { getScheduleWorkingPoints } from "../../../redux/api/expedition.api";
import { removeDuplicatesFromArray } from "../../../helpers/arrayHelper";
import { resetScheduleWorkingPoints } from "../../../redux/reducer/expedition.reducer";
import usePermissions from "../../../router/usePermissions";
import userRoles from "../../../constants/userRoles";

function SplitInvoices() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    routeParams: { op, delivery, status },
  } = useRoutesHelper() || {};

  const { isAuthorized } = usePermissions();

  const {
    picking: {
      deliveryInvoices: {
        data: allInvoices,
        isLoading: isLoadingDeliveryInvoices,
      },
    },
    expedition: {
      scheduleWorkingPoints: {
        data: scheduleWorkingPointsData,
        isLoadingScheduleWorkingPoints,
      },
    },
  } = useAppSelector((state) => ({
    picking: state.picking,
    expedition: state.expedition,
  }));

  const showSplitAndDeactivateButtons =
    Number(status) === 0 || Number(status) === 1;

  const [leftList, setLeftList] = useState<any[]>([]);
  const [rightList, setRightList] = useState<any[]>([]);
  const [isLoadingSplitting, setIsLoadingSplitting] = useState(false);

  const [valueZone, setValueZone] = useState<null | DropdownValue>(null);

  useEffect(() => {
    if (!op || !delivery) return;
    dispatch(getDeliveryInvoices({ params: { op } }));
  }, [dispatch, op, delivery]);

  useEffect(() => {
    if (allInvoices.length === 0) return;
    const availableInvoices = allInvoices.map((invoice) => invoice.act);
    setLeftList(availableInvoices);
  }, [allInvoices]);

  useEffect(() => {
    if (delivery) {
      dispatch(
        getScheduleWorkingPoints({
          page: 1,
          perPage: 50,
          deliveryPoint: delivery,
          route: "",
        }),
      );
    }
  }, [dispatch, delivery]);

  useEffect(() => {
    if (scheduleWorkingPointsData.workingPoints.length === 0) return;
    {
      const selectedValue = scheduleWorkingPointsData.workingPoints[0];
      if (selectedValue) {
        setValueZone((prev) => {
          if (prev === null) {
            return {
              id: selectedValue.idPctLucru,
              label: selectedValue.pctDeLivr,
            };
          }
          return prev;
        });
      }
    }
  }, [scheduleWorkingPointsData.workingPoints]);

  useEffect(() => {
    return () => {
      dispatch(resetScheduleWorkingPoints());
    };
  }, [dispatch]);

  const handleValidateSplit = (type: "split" | "disable" | "cancel") => {
    setIsLoadingSplitting(true);
    const callBack = () => {
      setIsLoadingSplitting(false);
      navigate(-1);
    };

    dispatch(
      postSplitDeliveryArrangements({
        params: { op, type, facturi: rightList, valueZone },
        callBack,
      }),
    );
  };

  let dropdownValues: DropdownValue[] =
    scheduleWorkingPointsData.workingPoints.map((item) => ({
      id: item.idPctLucru,
      label: item.pctDeLivr,
    }));

  if (valueZone) {
    dropdownValues = removeDuplicatesFromArray([...dropdownValues], "id");
  }

  return (
    <ContentCard
      CardHeader={() => {
        return (
          <div className="flex items-center">
            <Text variant="h5" gutterBottom className="mr-1 w-full">
              Split facturi din comanda {op} pentru punctul de livrare{" "}
            </Text>
            <Dropdown
              className="w-full"
              data={dropdownValues}
              singleValue={valueZone}
              setSingleValue={setValueZone}
              isLoading={isLoadingScheduleWorkingPoints}
            />
          </div>
        );
      }}>
      <TransferList
        isLoading={isLoadingDeliveryInvoices}
        leftListTitle="Facturi disponibile "
        rightListTitle="Facturi adaugate "
        leftListItems={leftList}
        rightListItems={rightList}
        listCrudItems={allInvoices}
        setRightList={setRightList}
        setLeftList={setLeftList}
        disabled={false}
      />
      <div className="flex justify-end mt-4">
        {showSplitAndDeactivateButtons && (
          <div>
            <CustomButton
              variant="outlined"
              className="mr-2"
              loading={isLoadingSplitting}
              disabled={isLoadingDeliveryInvoices || isLoadingSplitting}
              withConfirmationModal={{
                modalTitle: "Dezactiveaza facturi",
                modalSubtitle: "Esti sigur ca vrei sa dezactivezi facturile?",
                modalLeftButtonVariant: "outlined",
                modalRightButtonOnClick: () => handleValidateSplit("disable"),
              }}>
              Dezactiveaza facturi
            </CustomButton>

            <CustomButton
              variant="contained"
              className="mr-2"
              loading={isLoadingSplitting}
              disabled={isLoadingDeliveryInvoices || isLoadingSplitting}
              withConfirmationModal={{
                modalTitle: "Imparte comanda",
                modalSubtitle: "Esti sigur ca vrei sa imparti comanda?",
                modalLeftButtonVariant: "outlined",
                modalRightButtonOnClick: () => handleValidateSplit("split"),
              }}>
              Imparte comanda
            </CustomButton>
          </div>
        )}

        {isAuthorized(userRoles.SPLIT_AND_CANCEL_ORDER) && (
          <CustomButton
            variant="contained"
            color="error"
            className="mr-2"
            loading={isLoadingSplitting}
            disabled={isLoadingDeliveryInvoices || isLoadingSplitting}
            withConfirmationModal={{
              modalTitle: "Imparte si anuleaza comanda",
              modalSubtitle:
                "Esti sigur ca vrei sa imparti si sa anulezi comanda?",
              modalLeftButtonVariant: "outlined",
              modalRightButtonOnClick: () => handleValidateSplit("cancel"),
            }}>
            Imparte si anuleaza comanda
          </CustomButton>
        )}
      </div>
    </ContentCard>
  );
}

export default SplitInvoices;
